import { planAssignmentSteps } from '../../utils/assignment-steps';
import type { UnconnectedChannel } from '../../utils';

export type AssignConnection = (payload: {
  pypeId: string;
  env: 'live' | 'sandbox';
  fromConnectionId?: string;
  toConnectionId?: string;
}) => Promise<void>;

export interface ConnectChannelDeps {
  /** Resolves when the machine can take the next assignment (`idle`). */
  waitForIdle: () => Promise<void>;
  /** Dispatches ONE `assignPypeConnection` and settles with its outcome. */
  assign: AssignConnection;
  /** `false` once the page is gone: the parked dispatch must not fire. */
  isMounted: () => boolean;
}

/**
 * Connects a not-connected channel to a supported platform and detaches it from
 * AgentPro in the same gesture, so it is never left attached to two connections
 * (PSTUDIO-4344).
 *
 * The invoke detaches ONE connection per call (`fromConnectionId`), so a pype
 * that is somehow bound to several AgentPro connections gets one call per extra
 * binding first, and the last call carries the new connection. Rejects when any
 * call fails or the page went away while parked; calls already made are not
 * undone, so a partial failure leaves the channel unconnected, never on two.
 */
export const connectChannel = async (
  { channel, agentProConnectionIds }: UnconnectedChannel,
  toConnectionId: string,
  { waitForIdle, assign, isMounted }: ConnectChannelDeps
): Promise<void> => {
  const steps = planAssignmentSteps(agentProConnectionIds, toConnectionId).map(
    (step) => ({ pypeId: channel.pypeId, env: channel.env, ...step })
  );

  for (const step of steps) {
    // eslint-disable-next-line no-await-in-loop
    await waitForIdle();
    if (!isMounted()) {
      throw new Error('save-abandoned');
    }
    // eslint-disable-next-line no-await-in-loop
    await assign(step);
  }
};
